import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Set true when the backend told us at register/login that this client
  // has unfulfilled REQUIRED intake document requests and the system is
  // configured to block signup. ProtectedRoute reads this and redirects
  // to /intake-required. Persisted to sessionStorage so a hard reload
  // doesn't let the gate slip.
  const [requiresIntake, setRequiresIntake] = useState(
    sessionStorage.getItem('requires_intake_pending') === 'true'
  );

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const savedUser = sessionStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      auth.getMe()
        .then(response => {
          setUser(response.data);
          sessionStorage.setItem('user', JSON.stringify(response.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Internal: apply the requires_intake flag from a register/login response
  // to both state and sessionStorage in lock-step.
  const applyIntakeFlag = (flag) => {
    const truthy = !!flag;
    if (truthy) {
      sessionStorage.setItem('requires_intake_pending', 'true');
    } else {
      sessionStorage.removeItem('requires_intake_pending');
    }
    setRequiresIntake(truthy);
  };

  const login = async (email, password) => {
    const response = await auth.login({ email, password });
    const { token, user: userData, requires_intake } = response.data;

    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(userData));

    setUser(userData);
    applyIntakeFlag(requires_intake);

    return { ...userData, requires_intake: !!requires_intake };
  };

  const register = async (data) => {
    const response = await auth.register(data);
    const { token, user: userData, requires_intake } = response.data;

    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(userData));

    setUser(userData);
    applyIntakeFlag(requires_intake);

    return { ...userData, requires_intake: !!requires_intake };
  };

  // Called by the IntakeRequired page once every required+pending
  // request has been fulfilled. Lets the user out of the gate without
  // a hard refresh.
  const clearIntakeGate = () => applyIntakeFlag(false);

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('requires_intake_pending');
    setUser(null);
    setRequiresIntake(false);
  };

  // Drop a pre-built session payload (token + user + requires_intake)
  // straight into state + sessionStorage. Used by flows that already hit
  // a backend endpoint which returns the same shape as login/register —
  // currently the employee-invitation accept handler (roadmap item #17).
  // Saves the AcceptInvite page from re-implementing the persistence
  // dance and keeps a single source of truth for what "being logged in"
  // means.
  const applySession = ({ token, user: userData, requires_intake }) => {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    applyIntakeFlag(requires_intake);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    applySession,
    requiresIntake,
    clearIntakeGate,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isGroomer: user?.role === 'groomer',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
